/**
 * Caldwell Projection — Kettle Falls sales restated at Caldwell prices.
 *
 * IT IS A PROJECTION, NOT REVENUE, and the page is built so a reader cannot
 * miss that. The price-source counts sit ABOVE the total rather than in a
 * footnote, because a total built mostly from the 1.441 fallback is a
 * statement about the multiplier and not about Caldwell, and a reader is
 * entitled to know that before they read the number rather than after they
 * have quoted it.
 *
 * DATA READINESS IS FIRST, for the same reason. It is not diagnostics for a
 * developer — every figure in it is a load-bearing assumption underneath
 * Sections 1–4. It replaces reading caldwell_projection_audit on the console,
 * block for block and in the same order (Registers, A, B, C, E, F, Step zero),
 * and every block exports on its own so one can be handed over alone.
 *
 * Inline styles throughout: Tailwind utilities are not applied in this build
 * and fail silently, so they are not used anywhere here.
 */
import React, { useEffect, useState } from 'react';
import { adminReportsAPI } from '../../services/api';

// ---------------------------------------------------------------- types

interface Bucket {
  lines: number;
  tickets: number;
  lbs: number;
  actual_revenue: number;
  actual_profit: number;
  actual_margin_pct: number | null;
  projected_revenue: number;
  projected_profit: number;
  projected_margin_pct: number | null;
  difference: number;
  difference_pct: number | null;
  avg_ticket_actual: number | null;
  avg_ticket_projected: number | null;
  lines_per_ticket: number | null;
  lbs_per_ticket: number | null;
}

interface CoverageRow {
  bucket: string;
  label: string;
  skus: number;
  lines: number;
  revenue: number;
}

interface MilledPricingRow {
  sku: string;
  name: string;
  form: string;
  formula: string;
  retail_price: number;
  tv_retail_price: number | null;
  priced: boolean;
  sibling: string;
}

interface CatalogRow {
  brand: string;
  sku: string;
  name: string;
  method: string;
  msrp: number | null;
  kf_price: number;
  uplift: number | null;
  uplift_pct: number | null;
  revenue: number;
}

interface BulkSkuRow {
  sku: string;
  name: string;
  kind: string;
  is_byoc: boolean;
  milled: boolean;
  lines: number;
  revenue: number;
}

interface StepZero {
  milled_sku_count: number;
  milled_tv_priced: number;
  milled_tv_missing: number;
  third_party_sku_count: number;
  third_party_tv_priced: number;
  third_party_catalog_matched: Record<string, number>;
  third_party_catalog_matched_total: number;
  third_party_unmatched: number;
  third_party_unmatched_skus: { sku: string; name: string }[];
  registers: { location: string; register: string; transactions: number }[];
  coverage: CoverageRow[];
  coverage_unpriced_share_pct: number | null;
  milled_pricing: MilledPricingRow[];
  third_party_catalog_rows: CatalogRow[];
}

interface WeekdayRow {
  weekday: number;
  name: string;
  open_days: number;
  transactions: number;
  revenue: number;
  lbs: number;
  transactions_per_open_day: number | null;
  revenue_per_open_day: number | null;
  lbs_per_open_day: number | null;
  first_hour: number | null;
  last_hour: number | null;
}

interface SkuRow {
  sku: string;
  name: string;
  lines: number;
  revenue: number;
  lbs: number;
  distinct_buyers: number;
}

interface Cohort {
  month: string;
  new_members: number;
  organic: boolean;
  note: string;
  reached_2: number;
  reached_2_pct: number | null;
  median_days_to_2: number | null;
  reached_3: number;
  reached_3_pct: number | null;
  median_days_to_3: number | null;
  reached_4: number;
  reached_4_pct: number | null;
  median_days_to_4: number | null;
}

export interface CaldwellReport {
  date_from: string;
  date_to: string;
  step_zero: StepZero;
  section_1_restatement: {
    total: Bucket;
    caldwell_stocked_only: Bucket;
    by_category: Record<string, Bucket>;
    by_kind: Record<string, Bucket>;
    price_sources: {
      counts: Record<string, number>;
      revenue: Record<string, number>;
    };
    wholesale_layer: {
      totals: Bucket;
      invoice_payment_lines: number;
      invoice_payment_dollars: number;
      bulk_lines: number;
      bulk_dollars: number;
      bulk_skus: BulkSkuRow[];
    };
    excluded: {
      lines: number;
      dollars: number;
      skus: { sku: string; name: string; lines: number; revenue: number }[];
    };
    annualised: {
      open_days_observed: number;
      operating_days_assumed: number;
      actual: number;
      projected: number;
    } | null;
  };
  section_2_timing: {
    by_weekday: WeekdayRow[];
    by_hour: { hour: number; transactions: number; revenue: number; lbs: number }[];
    weekday_hour_grid: { weekday: number; hour: number; transactions: number }[];
    busiest_weekday: string | null;
    slowest_weekday: string | null;
    peak_two_hour_window: { start_hour: number; end_hour: number; transactions: number } | null;
    open_days_total: number;
  };
  section_3_basket: {
    per_visit: { visits: number; avg_lbs: number | null; avg_lines: number | null; avg_spend: number | null };
    share_by_category: Record<string, { revenue: number; revenue_share_pct: number | null; lbs: number; lbs_share_pct: number | null }>;
    share_by_kind: Record<string, { revenue: number; revenue_share_pct: number | null; lbs: number; lbs_share_pct: number | null }>;
    top_skus_by_revenue: SkuRow[];
    top_skus_by_lbs: SkuRow[];
    member_mix: { only_milled: number; only_third_party: number; both: number; members_counted: number };
  };
  section_4_growth: {
    cohorts: Cohort[];
    cadence: {
      pooled_median_days: number | null;
      pooled_intervals: number;
      median_of_member_medians: number | null;
      members_with_two_or_more_visits: number;
      definition: string;
    };
    first_purchase_basis: string;
  };
  caveats: Record<string, string>;
  constants: {
    fallback_uplift: number;
    annual_operating_days: number;
    caldwell_freight_per_lb: number;
    fallback_note: string;
    freight_note: string;
  };
}

// ------------------------------------------------------------- helpers

const money = (v: number | null | undefined) =>
  v === null || v === undefined
    ? '—'
    : v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** An em dash, never 0. A null margin means "no sales to have a margin on",
 *  and printing 0% invites the reader to average it in. */
const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v.toFixed(1)}%`;

const num = (v: number | null | undefined, digits = 0) =>
  v === null || v === undefined ? '—' : v.toLocaleString('en-US', {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  });

const hour = (h: number | null) => {
  if (h === null) return '—';
  const suffix = h < 12 ? 'am' : 'pm';
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return `${twelve}${suffix}`;
};

const SOURCE_LABEL: Record<string, string> = {
  tv_retail_price: 'Caldwell price (set)',
  catalog_msrp: 'Catalog MSRP',
  fallback_uplift: 'FALLBACK ×1.441',
  excluded_unpriceable: 'Excluded',
};

const S: Record<string, React.CSSProperties> = {
  section: { marginBottom: 34 },
  h2: { fontSize: 17, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' },
  h3: { fontSize: 14, fontWeight: 700, color: '#334155', margin: '18px 0 8px' },
  lede: { fontSize: 13, color: '#475569', margin: '0 0 12px', lineHeight: 1.55, maxWidth: 900 },
  cards: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 },
  card: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
    padding: '10px 14px', minWidth: 150,
  },
  cardLabel: { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 },
  cardValue: { fontSize: 19, fontWeight: 700, color: '#0f172a', marginTop: 3 },
  cardSub: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '7px 9px', borderBottom: '2px solid #cbd5e1',
    color: '#475569', fontWeight: 600, whiteSpace: 'nowrap',
  },
  thR: {
    textAlign: 'right', padding: '7px 9px', borderBottom: '2px solid #cbd5e1',
    color: '#475569', fontWeight: 600, whiteSpace: 'nowrap',
  },
  td: { padding: '6px 9px', borderBottom: '1px solid #f1f5f9' },
  tdR: { padding: '6px 9px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' },
  scroll: { overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' },
  warn: {
    background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6,
    padding: '9px 12px', fontSize: 12.5, color: '#78350f', lineHeight: 1.5,
    margin: '10px 0',
  },
  note: {
    background: '#f8fafc', borderLeft: '3px solid #94a3b8', borderRadius: 4,
    padding: '9px 13px', fontSize: 12.5, color: '#475569', lineHeight: 1.55,
    margin: '6px 0',
  },
  exportBtn: {
    padding: '5px 11px', fontSize: 12, border: '1px solid #cbd5e1',
    borderRadius: 6, background: '#fff', color: '#334155', cursor: 'pointer',
  },
  rowBetween: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 },
  gap: { color: '#b91c1c', fontWeight: 600 },
  ok: { color: '#15803d', fontWeight: 600 },
};

// ------------------------------------------------------------ component

interface Props {
  dateFrom: string;
  dateTo: string;
  onExport: (section: string) => void;
}

const CaldwellProjection: React.FC<Props> = ({ dateFrom, dateTo, onExport }) => {
  const [data, setData] = useState<CaldwellReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // `alive` guards against an out-of-order response overwriting a newer one
    // when the date range is changed twice quickly.
    let alive = true;
    setLoading(true);
    setError(null);
    adminReportsAPI
      .getCaldwellProjection({ date_from: dateFrom, date_to: dateTo })
      .then((res) => { if (alive) setData(res.data); })
      .catch((err) => {
        if (alive) setError(err?.response?.data?.error || 'Could not load the projection.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [dateFrom, dateTo]);

  if (loading && !data) return <p style={S.lede}>Loading…</p>;
  if (error) return <div style={S.warn}>{error}</div>;
  if (!data) return null;

  const s1 = data.section_1_restatement;
  const s2 = data.section_2_timing;
  const s3 = data.section_3_basket;
  const s4 = data.section_4_growth;
  const z = data.step_zero;

  // The readiness rows arrived in a later build than the sections; an older
  // API answering a newer page renders empty blocks rather than crashing.
  const coverage = z.coverage || [];
  const milledPricing = z.milled_pricing || [];
  const catalogRows = z.third_party_catalog_rows || [];
  const bulkSkus = s1.wholesale_layer.bulk_skus || [];
  const fallback = data.constants.fallback_uplift;

  const sourceCounts = s1.price_sources.counts || {};
  const totalSourced = Object.values(sourceCounts).reduce((a, b) => a + b, 0);
  const fallbackLines = sourceCounts.fallback_uplift || 0;
  const fallbackShare = totalSourced ? (fallbackLines / totalSourced) * 100 : 0;

  const ExportBtn: React.FC<{ section: string; label?: string }> = ({ section, label }) => (
    <button type="button" style={S.exportBtn} onClick={() => onExport(section)}>
      {label || 'Export CSV'}
    </button>
  );

  const Cmp: React.FC<{ b: Bucket; label: string }> = ({ b, label }) => (
    <tr>
      <td style={S.td}>{label}</td>
      <td style={S.tdR}>{num(b.lines)}</td>
      <td style={S.tdR}>{num(b.tickets)}</td>
      <td style={S.tdR}>{num(b.lbs, 1)}</td>
      <td style={S.tdR}>{money(b.actual_revenue)}</td>
      <td style={S.tdR}>{pct(b.actual_margin_pct)}</td>
      <td style={S.tdR}>{money(b.projected_revenue)}</td>
      <td style={S.tdR}>{pct(b.projected_margin_pct)}</td>
      <td style={{ ...S.tdR, fontWeight: 600, color: b.difference >= 0 ? '#15803d' : '#b91c1c' }}>
        {money(b.difference)}
      </td>
      <td style={S.tdR}>{pct(b.difference_pct)}</td>
    </tr>
  );

  return (
    <div>
      {/* ─────────────────────────── caveats, first ─────────────────── */}
      <div style={S.note}>
        <strong>This is a projection, not revenue.</strong> {data.caveats.window}
      </div>
      <div style={S.note}>{data.caveats.dating}</div>
      <div style={S.note}>{data.caveats.attribution}</div>
      <div style={S.note}>{data.caveats.ghosts}</div>
      <div style={S.note}>{data.caveats.freight}</div>
      <div style={S.note}>{data.caveats.shelf}</div>

      {/* ═══════════════════ DATA READINESS ═══════════════════════════ */}
      <div style={S.section}>
        <div style={S.rowBetween}>
          <h2 style={S.h2}>Data readiness</h2>
          <ExportBtn section="readiness" label="Export all blocks (CSV)" />
        </div>
        <p style={S.lede}>
          Every figure below is an assumption underneath Sections 1–4, computed by
          the same functions as the <code>caldwell_projection_audit</code> command
          and laid out in its order: Registers, A, B, C, E, F, Step zero. (D, the
          invoice-payment lines, is the wholesale layer under Section 1.) Read it
          before the totals: if most milled SKUs have no Caldwell price, the
          restatement is largely a statement about the ×{fallback} multiplier.
        </p>

        {/* ── REGISTERS ─────────────────────────────────────────────── */}
        <div style={S.rowBetween}>
          <h3 style={S.h3}>Registers</h3>
          <ExportBtn section="readiness_registers" />
        </div>
        <p style={S.lede}>
          Explains the differently-named rows in the member export. No store filter
          is applied anywhere on this page; every POS sale is Kettle Falls today.
        </p>
        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Location</th>
                <th style={S.th}>Register</th>
                <th style={S.thR}>Transactions</th>
              </tr>
            </thead>
            <tbody>
              {z.registers.map((r, i) => (
                <tr key={i}>
                  <td style={S.td}>{r.location}</td>
                  <td style={S.td}>{r.register}</td>
                  <td style={S.tdR}>{num(r.transactions)}</td>
                </tr>
              ))}
              {z.registers.length === 0 && (
                <tr><td style={S.td} colSpan={3}>No completed sales in range.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── A ─────────────────────────────────────────────────────── */}
        <div style={S.rowBetween}>
          <h3 style={S.h3}>A · Caldwell price coverage</h3>
          <ExportBtn section="readiness_a" />
        </div>
        <div style={S.cards}>
          <div style={S.card}>
            <div style={S.cardLabel}>Milled, priced</div>
            <div style={S.cardValue}>{num(z.milled_tv_priced)}</div>
            <div style={S.cardSub}>of {num(z.milled_sku_count)} sold in range</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Milled, no price</div>
            <div style={{ ...S.cardValue, ...(z.milled_tv_missing ? S.gap : S.ok) }}>
              {num(z.milled_tv_missing)}
            </div>
            <div style={S.cardSub}>these fall to the ×{fallback} uplift</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Third-party, priced</div>
            <div style={S.cardValue}>{num(z.third_party_tv_priced)}</div>
            <div style={S.cardSub}>of {num(z.third_party_sku_count)} sold in range</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Matched to catalog</div>
            <div style={S.cardValue}>{num(z.third_party_catalog_matched_total)}</div>
            <div style={S.cardSub}>priced at distributor MSRP</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Unmatched</div>
            <div style={{ ...S.cardValue, ...(z.third_party_unmatched ? S.gap : S.ok) }}>
              {num(z.third_party_unmatched)}
            </div>
            <div style={S.cardSub}>excluded from the restatement</div>
          </div>
        </div>
        <p style={S.lede}>
          Lines and dollars per bucket, so the counts above reconcile to the line
          table rather than standing alone. Priced means <code>tv_retail_price</code>{' '}
          above zero; milled means a feed formula is attached. Lines with no product
          (invoice payments, wholesale snapshot lines) are in neither bucket and are
          shown so the total closes.
        </p>
        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Bucket</th>
                <th style={S.thR}>SKUs</th>
                <th style={S.thR}>Lines</th>
                <th style={S.thR}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {coverage.map((r) => (
                <tr key={r.bucket}>
                  <td style={S.td}>{r.label}</td>
                  <td style={S.tdR}>{num(r.skus)}</td>
                  <td style={S.tdR}>{num(r.lines)}</td>
                  <td style={S.tdR}>{money(r.revenue)}</td>
                </tr>
              ))}
              {coverage.length > 0 && (
                <tr style={{ background: '#f8fafc' }}>
                  <td style={{ ...S.td, fontWeight: 600 }}>TOTAL</td>
                  <td style={S.tdR} />
                  <td style={{ ...S.tdR, fontWeight: 600 }}>
                    {num(coverage.reduce((a, r) => a + r.lines, 0))}
                  </td>
                  <td style={{ ...S.tdR, fontWeight: 600 }}>
                    {money(coverage.reduce((a, r) => a + r.revenue, 0))}
                  </td>
                </tr>
              )}
              {coverage.length === 0 && (
                <tr><td style={S.td} colSpan={4}>No lines in range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {z.coverage_unpriced_share_pct !== null && z.coverage_unpriced_share_pct !== undefined && (
          <div style={z.coverage_unpriced_share_pct > 50 ? S.warn : S.note}>
            <strong>{pct(z.coverage_unpriced_share_pct)} of line revenue has no Caldwell price set</strong>
            {' '}— milled lines in that share fall to the ×{fallback} uplift, third-party
            lines to a catalog MSRP or to exclusion.
          </div>
        )}

        {/* ── B ─────────────────────────────────────────────────────── */}
        <div style={S.rowBetween}>
          <h3 style={S.h3}>B · Milled pricing — every milled SKU sold in range</h3>
          <ExportBtn section="readiness_b" />
        </div>
        <p style={S.lede}>
          <code>tv_retail_price</code> is a per-SKU column, never a multiplier. No
          automated writer touches milled rows, so any Caldwell price here was typed
          by a person. A seasonal sibling is the formula with the same animal and
          grade on the other side of the season; if the sibling is unpriced, the
          fallback takes over the day the season turns.
        </p>
        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>SKU</th>
                <th style={S.th}>Name</th>
                <th style={S.th}>Form</th>
                <th style={S.th}>Formula</th>
                <th style={S.thR}>KF retail</th>
                <th style={S.thR}>Caldwell price</th>
                <th style={S.th}>Source</th>
                <th style={S.th}>Seasonal sibling</th>
              </tr>
            </thead>
            <tbody>
              {milledPricing.map((r) => (
                <tr key={r.sku}>
                  <td style={S.td}>{r.sku}</td>
                  <td style={S.td}>{r.name}</td>
                  <td style={S.td}>{r.form}</td>
                  <td style={S.td}>{r.formula}</td>
                  <td style={S.tdR}>{money(r.retail_price)}</td>
                  <td style={S.tdR}>{r.tv_retail_price === null ? 'NULL' : money(r.tv_retail_price)}</td>
                  <td style={{ ...S.td, ...(r.priced ? S.ok : S.gap) }}>
                    {r.priced ? 'set' : `FALLBACK ×${fallback}`}
                  </td>
                  <td style={{ ...S.td, fontSize: 12 }}>{r.sibling}</td>
                </tr>
              ))}
              {milledPricing.length === 0 && (
                <tr><td style={S.td} colSpan={8}>No milled SKUs sold in range.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── C ─────────────────────────────────────────────────────── */}
        <div style={S.rowBetween}>
          <h3 style={S.h3}>C · Third-party catalog match, by brand</h3>
          <ExportBtn section="readiness_c" />
        </div>
        <p style={S.lede}>
          Third-party SKUs with no Caldwell price, matched to the distributor catalog
          in order: exact UPC, normalised UPC, digit variants, supplier item number,
          name + weight. Grouped by brand because pricing gaps cluster by vendor and
          are fixed by vendor. A NO MATCH row is excluded from the restatement
          entirely rather than zeroed into it; its actual revenue is in the excluded
          block under Section 1.
        </p>
        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Match method</th>
                <th style={S.thR}>SKUs</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(z.third_party_catalog_matched).map(([method, n]) => (
                <tr key={method}>
                  <td style={S.td}>{method}</td>
                  <td style={S.tdR}>{num(n)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ ...S.td, ...(z.third_party_unmatched ? S.gap : {}) }}>NO MATCH</td>
                <td style={S.tdR}>{num(z.third_party_unmatched)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ height: 10 }} />
        <BrandGroupedCatalog rows={catalogRows} />

        {/* ── E ─────────────────────────────────────────────────────── */}
        <div style={S.rowBetween}>
          <h3 style={S.h3}>E · Bulk</h3>
          <ExportBtn section="readiness_e" />
        </div>
        <p style={S.lede}>
          Bulk is detected by the name rule the till actually prices on (BYOC first,
          then 2000, then 1000) because there is no bulk field to read. It sits in
          the wholesale layer and is never restated.
        </p>
        <div style={S.cards}>
          <div style={S.card}>
            <div style={S.cardLabel}>Bulk lines</div>
            <div style={S.cardValue}>{num(s1.wholesale_layer.bulk_lines)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Bulk dollars</div>
            <div style={S.cardValue}>{money(s1.wholesale_layer.bulk_dollars)}</div>
            <div style={S.cardSub}>at actual, not restated</div>
          </div>
        </div>
        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>SKU</th>
                <th style={S.th}>Name</th>
                <th style={S.th}>Kind</th>
                <th style={S.th}>BYOC flag</th>
                <th style={S.th}>Milled</th>
                <th style={S.thR}>Lines</th>
                <th style={S.thR}>Revenue (actual)</th>
              </tr>
            </thead>
            <tbody>
              {bulkSkus.map((r) => (
                <tr key={r.sku}>
                  <td style={S.td}>{r.sku}</td>
                  <td style={S.td}>{r.name}</td>
                  <td style={S.td}>{r.kind}</td>
                  <td style={{ ...S.td, ...(r.is_byoc ? S.gap : {}) }}>
                    {r.is_byoc ? 'yes — BYOC wins the pricing chain' : 'no'}
                  </td>
                  <td style={S.td}>{r.milled ? 'yes' : 'no — harmless until a formula is attached'}</td>
                  <td style={S.tdR}>{num(r.lines)}</td>
                  <td style={S.tdR}>{money(r.revenue)}</td>
                </tr>
              ))}
              {bulkSkus.length === 0 && (
                <tr><td style={S.td} colSpan={7}>No bulk lines in range.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── F ─────────────────────────────────────────────────────── */}
        <div style={S.rowBetween}>
          <h3 style={S.h3}>F · Open days</h3>
          <ExportBtn section="readiness_f" />
        </div>
        <p style={S.lede}>
          The actual operating pattern, Pacific time. An open day is a day with at
          least one completed sale. Section 2 divides by these, never by calendar
          days, and the annualised line in Section 1 scales by them.
        </p>
        <div style={S.cards}>
          <div style={S.card}>
            <div style={S.cardLabel}>Open days</div>
            <div style={S.cardValue}>{num(s2.open_days_total)}</div>
            <div style={S.cardSub}>days with at least one sale</div>
          </div>
          {s2.busiest_weekday && (
            <div style={S.card}>
              <div style={S.cardLabel}>Busiest</div>
              <div style={S.cardValue}>{s2.busiest_weekday}</div>
              <div style={S.cardSub}>per open day</div>
            </div>
          )}
          {s2.slowest_weekday && (
            <div style={S.card}>
              <div style={S.cardLabel}>Slowest</div>
              <div style={S.cardValue}>{s2.slowest_weekday}</div>
              <div style={S.cardSub}>per open day</div>
            </div>
          )}
        </div>
        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Weekday</th>
                <th style={S.thR}>Open days</th>
                <th style={S.thR}>Txns</th>
                <th style={S.thR}>First sale</th>
                <th style={S.thR}>Last sale</th>
              </tr>
            </thead>
            <tbody>
              {s2.by_weekday.map((r) => (
                <tr key={r.weekday}>
                  <td style={S.td}>{r.name}</td>
                  <td style={S.tdR}>{num(r.open_days)}</td>
                  <td style={S.tdR}>{num(r.transactions)}</td>
                  <td style={S.tdR}>{hour(r.first_hour)}</td>
                  <td style={S.tdR}>{hour(r.last_hour)}</td>
                </tr>
              ))}
              {s2.by_weekday.length === 0 && (
                <tr><td style={S.td} colSpan={5}>No completed sales in range.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── STEP ZERO ─────────────────────────────────────────────── */}
        <div style={S.rowBetween}>
          <h3 style={S.h3}>Step zero · the payload, verbatim</h3>
          <ExportBtn section="readiness_step_zero" />
        </div>
        <p style={S.lede}>
          The facts exactly as the API returns them, so this page and a console run
          of the audit command can be checked against each other. Same function,
          same numbers.
        </p>
        <div style={S.scroll}>
          <table style={{ ...S.table, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={S.th}>key</th>
                <th style={S.thR}>value</th>
              </tr>
            </thead>
            <tbody>
              {([
                ['milled_sku_count', z.milled_sku_count],
                ['milled_tv_priced', z.milled_tv_priced],
                ['milled_tv_missing', z.milled_tv_missing],
                ['third_party_sku_count', z.third_party_sku_count],
                ['third_party_tv_priced', z.third_party_tv_priced],
                ['third_party_catalog_matched_total', z.third_party_catalog_matched_total],
                ['third_party_unmatched', z.third_party_unmatched],
              ] as [string, number][]).map(([k, v]) => (
                <tr key={k}>
                  <td style={S.td}>{k}</td>
                  <td style={S.tdR}>{v}</td>
                </tr>
              ))}
              {Object.entries(z.third_party_catalog_matched).sort().map(([method, n]) => (
                <tr key={`m-${method}`}>
                  <td style={S.td}>third_party_catalog_matched.{method}</td>
                  <td style={S.tdR}>{n}</td>
                </tr>
              ))}
              {z.third_party_unmatched_skus.map((r) => (
                <tr key={`u-${r.sku}`}>
                  <td style={S.td}>unmatched_sku</td>
                  <td style={S.tdR}>{r.sku} {r.name}</td>
                </tr>
              ))}
              {z.third_party_unmatched > z.third_party_unmatched_skus.length && (
                <tr>
                  <td style={S.td} colSpan={2}>
                    … {num(z.third_party_unmatched - z.third_party_unmatched_skus.length)} more
                    unmatched SKU(s); block C above lists every one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══════════════════ 1 · RESTATEMENT ══════════════════════════ */}
      <div style={S.section}>
        <div style={S.rowBetween}>
          <h2 style={S.h2}>1 · Restatement</h2>
          <ExportBtn section="restatement" />
        </div>

        <h3 style={S.h3}>Where each Caldwell price came from</h3>
        <p style={S.lede}>
          Read this before the total. A Caldwell price somebody set and a
          distributor MSRP are real numbers; a FALLBACK line is a Kettle Falls
          price with a ×{data.constants.fallback_uplift} multiplier on it and is
          not a Caldwell price at all.
        </p>
        <div style={S.cards}>
          {Object.entries(sourceCounts).map(([source, n]) => (
            <div key={source} style={S.card}>
              <div style={S.cardLabel}>{SOURCE_LABEL[source] || source}</div>
              <div style={{
                ...S.cardValue,
                ...(source === 'fallback_uplift' && fallbackShare > 50 ? S.gap : {}),
              }}>
                {num(n)}
              </div>
              <div style={S.cardSub}>
                {money(s1.price_sources.revenue[source])}
                {totalSourced ? ` · ${((n / totalSourced) * 100).toFixed(0)}% of lines` : ''}
              </div>
            </div>
          ))}
        </div>
        {fallbackShare > 50 && (
          <div style={S.warn}>
            <strong>{fallbackShare.toFixed(0)}% of priced lines are FALLBACK.</strong>{' '}
            {data.constants.fallback_note}
          </div>
        )}
        <div style={S.note}>{data.constants.freight_note}</div>

        <h3 style={S.h3}>Actual against Caldwell</h3>
        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Cut</th>
                <th style={S.thR}>Lines</th>
                <th style={S.thR}>Tickets</th>
                <th style={S.thR}>lbs</th>
                <th style={S.thR}>Actual rev</th>
                <th style={S.thR}>Actual margin</th>
                <th style={S.thR}>Caldwell rev</th>
                <th style={S.thR}>Caldwell margin</th>
                <th style={S.thR}>Difference</th>
                <th style={S.thR}>%</th>
              </tr>
            </thead>
            <tbody>
              <Cmp b={s1.total} label="TOTAL (restated)" />
              <Cmp b={s1.caldwell_stocked_only} label="Caldwell-stocked only" />
              {Object.entries(s1.by_kind).map(([k, b]) => (
                <Cmp key={k} b={b} label={`kind · ${k}`} />
              ))}
              {Object.entries(s1.by_category).map(([k, b]) => (
                <Cmp key={k} b={b} label={`category · ${k}`} />
              ))}
            </tbody>
          </table>
        </div>
        <div style={S.note}>{data.caveats.shelf}</div>

        {s1.annualised && (
          <>
            <h3 style={S.h3}>Annualised</h3>
            <p style={S.lede}>
              Scaled by <strong>open days observed</strong> ({s1.annualised.open_days_observed}),
              never calendar days — a store closed on Sunday does not earn on Sunday —
              then multiplied by {s1.annualised.operating_days_assumed} operating days.
            </p>
            <div style={S.cards}>
              <div style={S.card}>
                <div style={S.cardLabel}>Actual, annualised</div>
                <div style={S.cardValue}>{money(s1.annualised.actual)}</div>
              </div>
              <div style={S.card}>
                <div style={S.cardLabel}>Caldwell, annualised</div>
                <div style={S.cardValue}>{money(s1.annualised.projected)}</div>
              </div>
            </div>
          </>
        )}

        <h3 style={S.h3}>Wholesale layer — shown at actual, never restated</h3>
        <div style={S.note}>{data.caveats.wholesale}</div>
        <div style={S.cards}>
          <div style={S.card}>
            <div style={S.cardLabel}>Invoice payments</div>
            <div style={S.cardValue}>{money(s1.wholesale_layer.invoice_payment_dollars)}</div>
            <div style={S.cardSub}>
              {num(s1.wholesale_layer.invoice_payment_lines)} lines · already in wholesale revenue
            </div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Bulk</div>
            <div style={S.cardValue}>{money(s1.wholesale_layer.bulk_dollars)}</div>
            <div style={S.cardSub}>{num(s1.wholesale_layer.bulk_lines)} lines</div>
          </div>
        </div>

        <h3 style={S.h3}>Excluded — could not be priced</h3>
        <p style={S.lede}>
          {num(s1.excluded.lines)} line(s), {money(s1.excluded.dollars)} of actual
          revenue. Dropped from the restatement rather than zeroed into it: zeroing
          keeps the cost while losing the revenue and quietly understates margin.
        </p>
        {s1.excluded.skus.length > 0 && (
          <div style={S.scroll}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>SKU</th><th style={S.th}>Name</th>
                  <th style={S.thR}>Lines</th><th style={S.thR}>Actual revenue</th>
                </tr>
              </thead>
              <tbody>
                {s1.excluded.skus.map((r) => (
                  <tr key={r.sku}>
                    <td style={S.td}>{r.sku}</td>
                    <td style={S.td}>{r.name}</td>
                    <td style={S.tdR}>{num(r.lines)}</td>
                    <td style={S.tdR}>{money(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═══════════════════ 2 · WHEN THEY COME ═══════════════════════ */}
      <div style={S.section}>
        <div style={S.rowBetween}>
          <h2 style={S.h2}>2 · When customers come</h2>
          <ExportBtn section="timing" />
        </div>
        <p style={S.lede}>
          Everything is <strong>per open day</strong>, never per calendar day. A
          Sunday with no sales is a closed Sunday, not a slow one, and averaging it
          in manufactures a dip no staffing decision should rest on. Sales with no
          member attached are included — they are real sales.
        </p>
        {s2.peak_two_hour_window && (
          <div style={S.cards}>
            <div style={S.card}>
              <div style={S.cardLabel}>Peak two hours</div>
              <div style={S.cardValue}>
                {hour(s2.peak_two_hour_window.start_hour)}–{hour(s2.peak_two_hour_window.end_hour)}
              </div>
              <div style={S.cardSub}>{num(s2.peak_two_hour_window.transactions)} transactions</div>
            </div>
          </div>
        )}
        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Weekday</th>
                <th style={S.thR}>Open days</th>
                <th style={S.thR}>Txns</th>
                <th style={S.thR}>Txns / open day</th>
                <th style={S.thR}>Revenue / open day</th>
                <th style={S.thR}>lbs / open day</th>
                <th style={S.thR}>First</th>
                <th style={S.thR}>Last</th>
              </tr>
            </thead>
            <tbody>
              {s2.by_weekday.map((r) => (
                <tr key={r.weekday} style={
                  r.name === s2.busiest_weekday ? { background: '#f0fdf4' }
                    : r.name === s2.slowest_weekday ? { background: '#fef2f2' } : undefined
                }>
                  <td style={S.td}>
                    {r.name}
                    {r.name === s2.busiest_weekday && ' · busiest'}
                    {r.name === s2.slowest_weekday && ' · slowest'}
                  </td>
                  <td style={S.tdR}>{num(r.open_days)}</td>
                  <td style={S.tdR}>{num(r.transactions)}</td>
                  <td style={S.tdR}>{num(r.transactions_per_open_day, 2)}</td>
                  <td style={S.tdR}>{money(r.revenue_per_open_day)}</td>
                  <td style={S.tdR}>{num(r.lbs_per_open_day, 1)}</td>
                  <td style={S.tdR}>{hour(r.first_hour)}</td>
                  <td style={S.tdR}>{hour(r.last_hour)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 style={S.h3}>Weekday × hour</h3>
        <WeekdayHourGrid grid={s2.weekday_hour_grid} weekdays={s2.by_weekday} />
      </div>

      {/* ═══════════════════ 3 · WHAT THEY CARRY OUT ══════════════════ */}
      <div style={S.section}>
        <div style={S.rowBetween}>
          <h2 style={S.h2}>3 · What they carry out</h2>
          <ExportBtn section="basket" />
        </div>
        <div style={S.cards}>
          <div style={S.card}>
            <div style={S.cardLabel}>Visits</div>
            <div style={S.cardValue}>{num(s3.per_visit.visits)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Avg spend</div>
            <div style={S.cardValue}>{money(s3.per_visit.avg_spend)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Avg lbs</div>
            <div style={S.cardValue}>{num(s3.per_visit.avg_lbs, 1)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Avg lines</div>
            <div style={S.cardValue}>{num(s3.per_visit.avg_lines, 2)}</div>
          </div>
        </div>

        <h3 style={S.h3}>Share of spend and pounds</h3>
        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Group</th>
                <th style={S.thR}>Revenue</th><th style={S.thR}>% rev</th>
                <th style={S.thR}>lbs</th><th style={S.thR}>% lbs</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(s3.share_by_kind).map(([k, v]) => (
                <tr key={`kind-${k}`}>
                  <td style={S.td}>kind · {k}</td>
                  <td style={S.tdR}>{money(v.revenue)}</td>
                  <td style={S.tdR}>{pct(v.revenue_share_pct)}</td>
                  <td style={S.tdR}>{num(v.lbs, 1)}</td>
                  <td style={S.tdR}>{pct(v.lbs_share_pct)}</td>
                </tr>
              ))}
              {Object.entries(s3.share_by_category).map(([k, v]) => (
                <tr key={`cat-${k}`}>
                  <td style={S.td}>category · {k}</td>
                  <td style={S.tdR}>{money(v.revenue)}</td>
                  <td style={S.tdR}>{pct(v.revenue_share_pct)}</td>
                  <td style={S.tdR}>{num(v.lbs, 1)}</td>
                  <td style={S.tdR}>{pct(v.lbs_share_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 style={S.h3}>Top 20 SKUs by revenue</h3>
        <SkuTable rows={s3.top_skus_by_revenue} />
        <h3 style={S.h3}>Top 20 SKUs by pounds</h3>
        <SkuTable rows={s3.top_skus_by_lbs} />

        <h3 style={S.h3}>Member mix</h3>
        <p style={S.lede}>
          Of {num(s3.member_mix.members_counted)} members who bought anything in
          range. Distinct-buyer counts elsewhere on this page count named members
          only — a sale with no member has no identity to be distinct from.
        </p>
        <div style={S.cards}>
          <div style={S.card}>
            <div style={S.cardLabel}>Milled only</div>
            <div style={S.cardValue}>{num(s3.member_mix.only_milled)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Third-party only</div>
            <div style={S.cardValue}>{num(s3.member_mix.only_third_party)}</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Both</div>
            <div style={S.cardValue}>{num(s3.member_mix.both)}</div>
          </div>
        </div>
      </div>

      {/* ═══════════════════ 4 · HOW THE BASE GROWS ═══════════════════ */}
      <div style={S.section}>
        <div style={S.rowBetween}>
          <h2 style={S.h2}>4 · How the base grows</h2>
          <ExportBtn section="growth" />
        </div>
        <div style={S.note}>{s4.first_purchase_basis}</div>
        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Month</th>
                <th style={S.thR}>New members</th>
                <th style={S.thR}>2nd</th><th style={S.thR}>days</th>
                <th style={S.thR}>3rd</th><th style={S.thR}>days</th>
                <th style={S.thR}>4th</th><th style={S.thR}>days</th>
                <th style={S.th}>Reading</th>
              </tr>
            </thead>
            <tbody>
              {s4.cohorts.map((c) => (
                <tr key={c.month} style={c.organic ? undefined : { background: '#fffbeb' }}>
                  <td style={S.td}>{c.month}</td>
                  <td style={S.tdR}>{num(c.new_members)}</td>
                  <td style={S.tdR}>{num(c.reached_2)} ({pct(c.reached_2_pct)})</td>
                  <td style={S.tdR}>{num(c.median_days_to_2, 1)}</td>
                  <td style={S.tdR}>{num(c.reached_3)} ({pct(c.reached_3_pct)})</td>
                  <td style={S.tdR}>{num(c.median_days_to_3, 1)}</td>
                  <td style={S.tdR}>{num(c.reached_4)} ({pct(c.reached_4_pct)})</td>
                  <td style={S.tdR}>{num(c.median_days_to_4, 1)}</td>
                  <td style={{ ...S.td, fontSize: 12, color: c.organic ? '#15803d' : '#92400e' }}>
                    {c.note}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 style={S.h3}>Visit cadence — both medians</h3>
        <div style={S.note}>{s4.cadence.definition}</div>
        <div style={S.cards}>
          <div style={S.card}>
            <div style={S.cardLabel}>Pooled median</div>
            <div style={S.cardValue}>{num(s4.cadence.pooled_median_days, 1)}</div>
            <div style={S.cardSub}>{num(s4.cadence.pooled_intervals)} intervals</div>
          </div>
          <div style={S.card}>
            <div style={S.cardLabel}>Median of member medians</div>
            <div style={S.cardValue}>{num(s4.cadence.median_of_member_medians, 1)}</div>
            <div style={S.cardSub}>
              {num(s4.cadence.members_with_two_or_more_visits)} members with 2+ visits
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------- sub-components

const SkuTable: React.FC<{ rows: SkuRow[] }> = ({ rows }) => (
  <div style={S.scroll}>
    <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>SKU</th><th style={S.th}>Name</th>
          <th style={S.thR}>Lines</th><th style={S.thR}>Revenue</th>
          <th style={S.thR}>lbs</th><th style={S.thR}>Buyers</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.sku}>
            <td style={S.td}>{r.sku}</td>
            <td style={S.td}>{r.name}</td>
            <td style={S.tdR}>{num(r.lines)}</td>
            <td style={S.tdR}>{money(r.revenue)}</td>
            <td style={S.tdR}>{num(r.lbs, 1)}</td>
            <td style={S.tdR}>{num(r.distinct_buyers)}</td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td style={S.td} colSpan={6}>No sales in range.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

/** Unpriced third-party SKUs, grouped by the brand they matched under, with a
 *  subtotal row per brand. Gaps cluster by vendor and are fixed by vendor, so
 *  the grouping is the unit of work, not decoration. */
const BrandGroupedCatalog: React.FC<{ rows: CatalogRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <p style={S.lede}>
        No unpriced third-party SKUs sold in range — every third-party line has a
        Caldwell price set.
      </p>
    );
  }
  const groups = new Map<string, CatalogRow[]>();
  rows.forEach((r) => {
    const g = groups.get(r.brand) || [];
    g.push(r);
    groups.set(r.brand, g);
  });
  return (
    <div style={S.scroll}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>SKU</th>
            <th style={S.th}>Name</th>
            <th style={S.th}>Match</th>
            <th style={S.thR}>MSRP</th>
            <th style={S.thR}>KF price</th>
            <th style={S.thR}>Uplift $</th>
            <th style={S.thR}>Uplift %</th>
            <th style={S.thR}>Revenue in range</th>
          </tr>
        </thead>
        <tbody>
          {Array.from(groups.entries()).map(([brand, rs]) => {
            const matched = rs.filter((r) => r.method !== 'NO MATCH').length;
            const revenue = rs.reduce((a, r) => a + r.revenue, 0);
            return (
              <React.Fragment key={brand}>
                <tr style={{ background: '#f1f5f9' }}>
                  <td style={{ ...S.td, fontWeight: 700 }} colSpan={3}>
                    {brand} · {rs.length} SKU{rs.length === 1 ? '' : 's'}, {matched} matched
                    {matched < rs.length && (
                      <span style={S.gap}>, {rs.length - matched} unmatched</span>
                    )}
                  </td>
                  <td style={S.tdR} colSpan={4} />
                  <td style={{ ...S.tdR, fontWeight: 700 }}>{money(revenue)}</td>
                </tr>
                {rs.map((r) => (
                  <tr key={r.sku}>
                    <td style={S.td}>{r.sku}</td>
                    <td style={S.td}>{r.name}</td>
                    <td style={{ ...S.td, ...(r.method === 'NO MATCH' ? S.gap : {}) }}>{r.method}</td>
                    <td style={S.tdR}>{money(r.msrp)}</td>
                    <td style={S.tdR}>{money(r.kf_price)}</td>
                    <td style={S.tdR}>{money(r.uplift)}</td>
                    <td style={S.tdR}>{pct(r.uplift_pct)}</td>
                    <td style={S.tdR}>{money(r.revenue)}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/** Transaction counts per weekday and hour. Shaded by share of the busiest
 *  cell, so the trading shape is readable at a glance without a chart
 *  library — and an empty cell stays visibly empty rather than pale-but-present. */
const WeekdayHourGrid: React.FC<{
  grid: { weekday: number; hour: number; transactions: number }[];
  weekdays: WeekdayRow[];
}> = ({ grid, weekdays }) => {
  if (grid.length === 0) return <p style={S.lede}>No completed sales in range.</p>;
  const byKey = new Map(grid.map((g) => [`${g.weekday}-${g.hour}`, g.transactions]));
  const hours = Array.from(new Set(grid.map((g) => g.hour))).sort((a, b) => a - b);
  const max = Math.max(...grid.map((g) => g.transactions));

  return (
    <div style={S.scroll}>
      <table style={{ ...S.table, fontSize: 12 }}>
        <thead>
          <tr>
            <th style={S.th}>Weekday</th>
            {hours.map((h) => <th key={h} style={S.thR}>{hour(h)}</th>)}
          </tr>
        </thead>
        <tbody>
          {weekdays.map((w) => (
            <tr key={w.weekday}>
              <td style={S.td}>{w.name}</td>
              {hours.map((h) => {
                const n = byKey.get(`${w.weekday}-${h}`) || 0;
                return (
                  <td
                    key={h}
                    style={{
                      ...S.tdR,
                      background: n ? `rgba(37,99,235,${0.08 + 0.55 * (n / max)})` : undefined,
                      color: n ? '#0f172a' : '#cbd5e1',
                      fontWeight: n === max ? 700 : 400,
                    }}
                  >
                    {n || '·'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CaldwellProjection;
